import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { listAccountingInvoices } from "../../api/accountingApi";
import "./AdminAccountingFatturazione.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("it-IT");
}

function centsToEuroString(cents) {
  const n = Number(cents || 0);
  return (n / 100).toFixed(2).replace(".", ",");
}

function formatSourceType(inv) {
  const orders = Number(inv?.order_count) || 0;
  const subs = Number(inv?.subscription_count) || 0;

  if (orders > 0 && subs > 0) return `Mista (${orders} ord. · ${subs} abb.)`;
  if (subs > 0) return `Abbonamenti (${subs})`;
  return orders > 0 ? `Ordini (${orders})` : "—";
}

export default function AdminAccountingFattureEmesse() {
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const [invoiceRows, setInvoiceRows] = useState([]);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceTotalPages, setInvoiceTotalPages] = useState(1);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [invoiceFilters, setInvoiceFilters] = useState({
    year: "",
    fornitore_id: "",
  });

  const [invoiceSort, setInvoiceSort] = useState({
    field: "number", // "number" | "date" | "supplier" | "source" | "imponibile" | "totale"
    dir: "desc",
  });

  const token = localStorage.getItem("token");

  const downloadInvoicePdf = async (invoiceId) => {
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(
        `${API_BASE}/api/admin/accounting/invoices/${invoiceId}/pdf`,
        {
          method: "GET",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (!res.ok) {
        const js = await res.json().catch(() => ({}));
        toast.error(js.error || "Errore download PDF fattura");
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error("[FattureEmesse] downloadInvoicePdf error:", err);
      toast.error("Errore di rete durante il download del PDF");
    }
  };

  // ========== LOAD SUPPLIERS OPTIONS ==========
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingSuppliers(true);
        const res = await fetch(
          `${API_BASE}/api/admin/accounting/fornitori-options`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          },
        );
        const js = await res.json();
        if (!res.ok) throw new Error(js?.error || `HTTP ${res.status}`);
        if (!alive) return;
        setSuppliers(Array.isArray(js) ? js : []);
      } catch (err) {
        console.error("[FattureEmesse] load suppliers error:", err);
        if (alive) toast.error(err?.message || "Errore caricamento fornitori");
      } finally {
        if (alive) setLoadingSuppliers(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadInvoices(nextPage = 1, overrideFilters) {
    const effFilters = overrideFilters || invoiceFilters;

    try {
      setLoadingInvoices(true);
      const res = await listAccountingInvoices({
        page: nextPage,
        pageSize: 20,
        filters: effFilters,
      });

      setInvoiceRows(Array.isArray(res.data) ? res.data : []);
      setInvoicePage(res.page || nextPage);
      setInvoiceTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error("[FattureEmesse] loadInvoices error:", err);
      toast.error(err?.message || "Errore caricamento fatture proforma");
    } finally {
      setLoadingInvoices(false);
    }
  }

  useEffect(() => {
    loadInvoices(1, invoiceFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInvoiceYearChange = (e) => {
    const year = e.target.value;
    const nextFilters = { ...invoiceFilters, year };
    setInvoiceFilters(nextFilters);
    loadInvoices(1, nextFilters);
  };

  const handleInvoiceSupplierChange = (e) => {
    const fid = e.target.value;
    const nextFilters = { ...invoiceFilters, fornitore_id: fid };
    setInvoiceFilters(nextFilters);
    loadInvoices(1, nextFilters);
  };

  const handleInvoicePrevPage = () => {
    if (invoicePage <= 1 || loadingInvoices) return;
    loadInvoices(invoicePage - 1, invoiceFilters);
  };

  const handleInvoiceNextPage = () => {
    if (invoicePage >= invoiceTotalPages || loadingInvoices) return;
    loadInvoices(invoicePage + 1, invoiceFilters);
  };

  const handleInvoiceSort = (field) => {
    setInvoiceSort((prev) => {
      if (!prev || prev.field !== field) return { field, dir: "asc" };
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const renderInvoiceSortIcon = (field) => {
    if (!invoiceSort || invoiceSort.field !== field) {
      return <span className="acc2-sort-icon">↕</span>;
    }
    return (
      <span className="acc2-sort-icon acc2-sort-icon--active">
        {invoiceSort.dir === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const sortedInvoices = useMemo(() => {
    if (!invoiceRows || invoiceRows.length === 0) return [];

    const { field, dir } = invoiceSort || {};
    if (!field) return [...invoiceRows];

    const getVal = (inv) => {
      switch (field) {
        case "number":
          return {
            year: Number(inv.year) || 0,
            number: Number(inv.number) || 0,
            date: inv.invoice_date || inv.date || "",
            id: Number(inv.id) || 0,
          };
        case "date":
          return inv.invoice_date || inv.date || "";
        case "supplier":
          return (
            inv.fornitore_label ||
            inv.fornitore_nome ||
            inv.supplier_name ||
            inv.fornitore ||
            ""
          );
        case "source":
          return formatSourceType(inv);
        case "imponibile":
          return Number(inv.imponibile_cents) || 0;
        case "totale":
          return Number(inv.total_cents) || 0;
        default:
          return inv.invoice_date || inv.date || "";
      }
    };

    const arr = [...invoiceRows];

    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);

      if (field === "number") {
        if (va.year !== vb.year) {
          return dir === "asc" ? va.year - vb.year : vb.year - va.year;
        }

        if (va.number !== vb.number) {
          return dir === "asc" ? va.number - vb.number : vb.number - va.number;
        }

        const ta = va.date ? new Date(va.date).getTime() : 0;
        const tb = vb.date ? new Date(vb.date).getTime() : 0;
        if (ta !== tb) {
          return dir === "asc" ? ta - tb : tb - ta;
        }

        return dir === "asc" ? va.id - vb.id : vb.id - va.id;
      }

      if (field === "date") {
        const ta = va ? new Date(va).getTime() : 0;
        const tb = vb ? new Date(vb).getTime() : 0;

        if (ta !== tb) {
          return dir === "asc" ? ta - tb : tb - ta;
        }

        const na = Number(a.number) || 0;
        const nb = Number(b.number) || 0;
        if (na !== nb) {
          return dir === "asc" ? na - nb : nb - na;
        }

        const ida = Number(a.id) || 0;
        const idb = Number(b.id) || 0;
        return dir === "asc" ? ida - idb : idb - ida;
      }

      if (typeof va === "number" && typeof vb === "number") {
        if (va !== vb) {
          return dir === "asc" ? va - vb : vb - va;
        }

        const ida = Number(a.id) || 0;
        const idb = Number(b.id) || 0;
        return dir === "asc" ? ida - idb : idb - ida;
      }

      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();

      if (sa !== sb) {
        if (sa < sb) return dir === "asc" ? -1 : 1;
        return dir === "asc" ? 1 : -1;
      }

      const ida = Number(a.id) || 0;
      const idb = Number(b.id) || 0;
      return dir === "asc" ? ida - idb : idb - ida;
    });

    return arr;
  }, [invoiceRows, invoiceSort]);

  return (
    <div className="acc2-page">
      <div className="acc2-inner">
        <header className="acc2-header">
          <div>
            <h1>Contabilità · Fatture proforma</h1>
            <p className="acc2-header-sub">
              Elenco delle proforma emesse verso fornitori, con download PDF.
            </p>
          </div>
        </header>

        <section className="acc2-invoices-section" style={{ marginTop: 0 }}>
          <div className="acc2-invoices-header acc2-invoices-header--stack">
            <div className="acc2-invoices-headcopy">
              <h2 className="acc2-invoices-title">Fatture proforma</h2>
              <p className="acc2-invoices-sub">
                Filtra per anno e fornitore. Ordina e scarica i PDF.
              </p>
            </div>

            <div className="acc2-invoices-filters acc2-invoices-filters--left">
              <div className="acc2-filter-group acc2-filter-group--invoice-year">
                <label className="acc2-label">Anno</label>
                <input
                  type="number"
                  className="acc2-input acc2-input--sm"
                  value={invoiceFilters.year}
                  onChange={handleInvoiceYearChange}
                  placeholder="Es. 2025"
                />
              </div>

              <div className="acc2-filter-group acc2-filter-group--invoice-supplier">
                <label className="acc2-label">Fornitore</label>
                <select
                  className="acc2-input acc2-input--sm"
                  value={invoiceFilters.fornitore_id}
                  onChange={handleInvoiceSupplierChange}
                  disabled={loadingSuppliers}
                >
                  <option value="">Tutti</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="acc2-invoices-table-wrapper">
            <table className="acc2-invoices-table">
              <thead>
                <tr>
                  <th
                    className="acc2-inv-th acc2-inv-th--nr acc2-th--sortable"
                    onClick={() => handleInvoiceSort("number")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Nr / Anno</span>
                      {renderInvoiceSortIcon("number")}
                    </div>
                  </th>

                  <th
                    className="acc2-inv-th acc2-inv-th--date acc2-th--sortable"
                    onClick={() => handleInvoiceSort("date")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Data</span>
                      {renderInvoiceSortIcon("date")}
                    </div>
                  </th>

                  <th
                    className="acc2-inv-th acc2-inv-th--supplier acc2-th--sortable"
                    onClick={() => handleInvoiceSort("supplier")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Fornitore</span>
                      {renderInvoiceSortIcon("supplier")}
                    </div>
                  </th>

                  <th
                    className="acc2-inv-th acc2-inv-th--supplier acc2-th--sortable"
                    onClick={() => handleInvoiceSort("source")}
                  >
                    <div className="acc2-th-content">
                      <span className="acc2-th-label">Origine</span>
                      {renderInvoiceSortIcon("source")}
                    </div>
                  </th>

                  <th
                    className="acc2-inv-th acc2-inv-th--num acc2-th--sortable"
                    onClick={() => handleInvoiceSort("imponibile")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Imponibile</span>
                      {renderInvoiceSortIcon("imponibile")}
                    </div>
                  </th>

                  <th
                    className="acc2-inv-th acc2-inv-th--num acc2-th--sortable"
                    onClick={() => handleInvoiceSort("totale")}
                  >
                    <div className="acc2-th-content acc2-th-content--right">
                      <span className="acc2-th-label">Totale</span>
                      {renderInvoiceSortIcon("totale")}
                    </div>
                  </th>

                  <th className="acc2-inv-th acc2-inv-th--actions">Azioni</th>
                </tr>
              </thead>

              <tbody>
                {loadingInvoices && (
                  <tr>
                    <td colSpan={7} className="acc2-inv-empty">
                      Caricamento fatture…
                    </td>
                  </tr>
                )}

                {!loadingInvoices && sortedInvoices.length === 0 && (
                  <tr>
                    <td colSpan={7} className="acc2-inv-empty">
                      Nessuna fattura trovata con i filtri correnti.
                    </td>
                  </tr>
                )}

                {!loadingInvoices &&
                  sortedInvoices.length > 0 &&
                  sortedInvoices.map((inv) => (
                    <tr key={inv.id} className="acc2-inv-row">
                      <td className="acc2-inv-td acc2-inv-td--nr">
                        <span className="acc2-cell-main">
                          {String(inv.number).padStart(3, "0")}/{inv.year}
                        </span>
                      </td>
                      <td className="acc2-inv-td acc2-inv-td--date">
                        {formatDate(inv.invoice_date)}
                      </td>
                      <td className="acc2-inv-td acc2-inv-td--supplier">
                        {inv.fornitore_label}
                      </td>
                      <td className="acc2-inv-td acc2-inv-td--supplier">
                        {formatSourceType(inv)}
                      </td>
                      <td className="acc2-inv-td acc2-inv-td--num">
                        {centsToEuroString(inv.imponibile_cents)} €
                      </td>
                      <td className="acc2-inv-td acc2-inv-td--num">
                        {centsToEuroString(inv.total_cents)} €
                      </td>
                      <td className="acc2-inv-td acc2-inv-td--actions">
                        <button
                          type="button"
                          className="acc2-btn acc2-btn--ghost acc2-btn--sm"
                          onClick={() => downloadInvoicePdf(inv.id)}
                        >
                          Scarica PDF
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="acc2-invoices-pagination">
            <button
              type="button"
              className="acc2-btn acc2-btn--ghost acc2-btn--sm"
              onClick={handleInvoicePrevPage}
              disabled={loadingInvoices || invoicePage <= 1}
            >
              ←
            </button>
            <span className="acc2-page-info">
              Pagina {invoicePage} di {invoiceTotalPages}
            </span>
            <button
              type="button"
              className="acc2-btn acc2-btn--ghost acc2-btn--sm"
              onClick={handleInvoiceNextPage}
              disabled={loadingInvoices || invoicePage >= invoiceTotalPages}
            >
              →
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
